if (!customElements.get('snuggli-listicle')) {
  customElements.define(
    'snuggli-listicle',
    class extends HTMLElement {
      connectedCallback() {
        this.disconnectedCallback()
        this.setupUpdatedDate()
        this.setupShoppingBar()
        this.setupCountdown()
        this.setupVideos()
      }

      setupUpdatedDate() {
        const time = this.querySelector('[data-relative-updated-date]')
        if (!time) return
        this.refreshUpdatedDate = () => {
          clearTimeout(this.dateTimer)
          const now = new Date()
          const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 3, 12)
          time.dateTime = [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'), String(date.getDate()).padStart(2, '0')].join('-')
          time.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en', {
            month: 'short', day: '2-digit', year: 'numeric',
          }).format(date)
          const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)
          this.dateTimer = setTimeout(this.refreshUpdatedDate, tomorrow.getTime() - now.getTime() + 50)
        }
        this.refreshUpdatedDate()
        window.addEventListener('pageshow', this.refreshUpdatedDate)
        document.addEventListener('visibilitychange', this.refreshUpdatedDate)
      }

      setupShoppingBar() {
        const sticky = this.querySelector('[data-sticky]')
        const intro = this.querySelector('[data-intro]')
        const offerButton = this.dataset.offerTarget
          ? document.getElementById(this.dataset.offerTarget)
          : this.querySelector('[data-offer-button]')
        const promo = this.querySelector('.listicle-promo')
        if (!sticky || !intro || !offerButton) return
        sticky.hidden = true
        if (!('IntersectionObserver' in window)) return

        let lastHeight
        const observe = () => {
          const height = Math.ceil(promo?.getBoundingClientRect().height || 0)
          if (height === lastHeight) return
          lastHeight = height
          this.style.setProperty('--listicle-promo-height', `${height}px`)
          this.shoppingObserver?.disconnect()
          let introPassed = false
          let offerVisible = false
          this.shoppingObserver = new IntersectionObserver(
            (entries) => {
              for (const entry of entries) {
                if (entry.target === intro) introPassed = entry.boundingClientRect.bottom <= height
                if (entry.target === offerButton) offerVisible = entry.isIntersecting
              }
              sticky.hidden = !introPassed || offerVisible
            },
            { rootMargin: `-${height}px 0px 0px 0px` },
          )
          this.shoppingObserver.observe(intro)
          this.shoppingObserver.observe(offerButton)
        }
        observe()
        if (promo && 'ResizeObserver' in window) {
          this.promoObserver = new ResizeObserver(observe)
          this.promoObserver.observe(promo)
        }
      }

      setupCountdown() {
        const clock = this.querySelector('[data-countdown]')
        const assurance = this.querySelector('[data-assurance]')
        const digits = clock?.querySelector('[data-clock-digits]')
        if (!clock || !digits) return
        clock.hidden = true
        if (assurance) assurance.hidden = false
        if (this.dataset.countdownMode === 'recurring') {
          this.setupRecurringCountdown(clock, digits, assurance)
          return
        }
        const deadline = this.dataset.offerEndsAt || ''
        const expiresAt = /T.*(?:Z|[+-]\d{2}:\d{2})$/i.test(deadline) ? Date.parse(deadline) : NaN
        if (!Number.isFinite(expiresAt) || expiresAt <= Date.now()) return

        const update = () => {
          const seconds = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000))
          if (!seconds) {
            clock.hidden = true
            if (assurance) assurance.hidden = false
            clearInterval(this.timer)
            return
          }
          digits.textContent = [Math.floor(seconds / 3600), Math.floor(seconds / 60) % 60, seconds % 60]
            .map((part) => String(part).padStart(2, '0'))
            .join(':')
          clock.hidden = false
          if (assurance) assurance.hidden = true
        }
        this.timer = setInterval(update, 1000)
        update()
      }

      setupRecurringCountdown(clock, digits, assurance) {
        const duration = Number(this.dataset.countdownDuration)
        const key = this.dataset.countdownKey
        if (!key || !Number.isSafeInteger(duration) || duration <= 0) return
        const durationMs = duration * 1000
        if (!Number.isSafeInteger(durationMs)) return

        const savedVisit = this.recurringCountdownVisit
        let startedAt = savedVisit?.key === key ? savedVisit.startedAt : Date.now()
        const update = () => {
          const elapsed = Math.max(0, Date.now() - startedAt)
          const seconds = Math.ceil((durationMs - (elapsed % durationMs)) / 1000)
          digits.textContent = [Math.floor(seconds / 3600), Math.floor(seconds / 60) % 60, seconds % 60]
            .map((part) => String(part).padStart(2, '0'))
            .join(':')
          clock.hidden = false
          if (assurance) assurance.hidden = true
        }
        this.refreshCountdown = () => {
          try {
            const stored = window.localStorage.getItem(key)
            const timestamp = stored === null || stored.trim() === '' ? NaN : Number(stored)
            if (Number.isSafeInteger(timestamp) && timestamp >= 0 && timestamp <= Date.now()) {
              startedAt = timestamp
            } else {
              window.localStorage.setItem(key, String(startedAt))
            }
          } catch (_) {}
          this.recurringCountdownVisit = { key, startedAt }
          update()
        }
        this.onCountdownStorage = (event) => {
          if (event.key === key || event.key === null) this.refreshCountdown()
        }
        this.refreshCountdown()
        this.timer = setInterval(update, 1000)
        window.addEventListener('storage', this.onCountdownStorage)
        window.addEventListener('pageshow', this.refreshCountdown)
        document.addEventListener('visibilitychange', this.refreshCountdown)
      }

      setupVideos() {
        this.autoVideos = [...this.querySelectorAll('[data-autoplay-video="true"] video')]
        if (!this.autoVideos.length || !('IntersectionObserver' in window)) return
        this.motionPreference = window.matchMedia('(prefers-reduced-motion: reduce)')
        this.onMotionChange = () => {
          this.videoObserver?.disconnect()
          this.autoVideos.forEach((video) => video.pause())
          if (this.motionPreference.matches) return
          this.videoObserver = new IntersectionObserver(
            (entries) => {
              for (const { target: video, isIntersecting } of entries) {
                if (isIntersecting) {
                  video.muted = true
                  video.play()?.catch(() => {})
                } else {
                  video.pause()
                }
              }
            },
            { threshold: 0.25 },
          )
          this.autoVideos.forEach((video) => this.videoObserver.observe(video))
        }
        this.motionPreference.addEventListener('change', this.onMotionChange)
        this.onMotionChange()
      }

      disconnectedCallback() {
        clearTimeout(this.dateTimer)
        if (this.refreshUpdatedDate) {
          window.removeEventListener('pageshow', this.refreshUpdatedDate)
          document.removeEventListener('visibilitychange', this.refreshUpdatedDate)
        }
        this.shoppingObserver?.disconnect()
        this.promoObserver?.disconnect()
        this.videoObserver?.disconnect()
        this.motionPreference?.removeEventListener('change', this.onMotionChange)
        this.autoVideos?.forEach((video) => video.pause())
        clearInterval(this.timer)
        if (this.refreshCountdown) {
          window.removeEventListener('storage', this.onCountdownStorage)
          window.removeEventListener('pageshow', this.refreshCountdown)
          document.removeEventListener('visibilitychange', this.refreshCountdown)
        }
      }
    },
  )
}
